/**
 * AdminCobrancasTab — Gestão de cobranças fee-based mensal.
 * Sprint 6: Extraído de AdminPanel.jsx.
 * Sprint 6: Integração com Supabase (fetchCobrancas / upsertCobranca / fetchCompanySetting).
 * Sprint 7: Automação — auto-gera cobranças para novos clientes, auto-marca atrasados,
 *           atualiza patrimônio dos snapshots, gera mês seguinte automaticamente.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { fmtR, Card, STitle, Badge } from "./adminUtils.jsx";
import { CurrencyInput } from "../shared/CurrencyInput.jsx";
import { logWarn } from "../shared/logger.js";
import { SkeletonTable } from "../shared/Skeletons.jsx";
import { RADIUS } from "../shared/theme.js";
import {
  fetchCobrancas,
  upsertCobranca,
  updateCobranca,
  fetchCompanySetting,
  logAudit,
} from "../supabaseClient.js";
import PaymentModal from "./modals/PaymentModal.jsx";

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Gera label "Março de 2026" para o mês corrente */
function mesRefLabel(date = new Date()) {
  return date
    .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    .replace(/^./, (c) => c.toUpperCase());
}

/** Gera data de vencimento "15/04/2026" (dia 15 do mês seguinte) */
function vencimentoStr(date = new Date()) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 15).toLocaleDateString("pt-BR");
}

/** Parseia "15/04/2026" para Date */
function parseVencimento(str) {
  if (!str || str === "–") return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
}

export default function AdminCobrancasTab({ dynamicClients, setLembreteFor, setToast, T }) {
  const [cobrancas, setCobrancas] = useState([]);
  const [dadosBancarios, setDadosBancarios] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [autoLog, setAutoLog] = useState([]);
  const [paymentModalCobranca, setPaymentModalCobranca] = useState(null);

  const currentMesRef = useMemo(() => mesRefLabel(), []);
  const currentVenc = useMemo(() => vencimentoStr(), []);

  // ── Carrega cobranças e dados bancários do Supabase ──────────────────────
  const loadData = useCallback(async () => {
    try {
      const [rows, setting] = await Promise.all([
        fetchCobrancas().catch((err) => {
          logWarn("AdminCobrancas", "Fetch cobranças failed", { error: err?.message });
          return [];
        }),
        fetchCompanySetting("dados_bancarios").catch((err) => {
          logWarn("AdminCobrancas", "Fetch dados bancários failed", { error: err?.message });
          return null;
        }),
      ]);

      // Mapeia formato do banco para formato do componente
      const mapped = rows.map((r) => ({
        id: r.id,
        clientId: r.client_id,
        clientName: r.client_name,
        email: r.email,
        patrimonio: Number(r.patrimonio) || 0,
        feeAnual: Number(r.fee_anual) || 0.8,
        valor: Number(r.valor) || 0,
        refMes: r.ref_mes,
        vencimento: r.vencimento || "–",
        status: r.status,
        pagoEm: r.pago_em,
        obs: r.obs || "",
      }));

      // ── AUTOMAÇÃO 1: Auto-gerar cobranças para clientes sem registro no mês atual ──
      const existingClientIds = new Set(
        mapped.filter((c) => c.refMes === currentMesRef).map((c) => c.clientId)
      );
      const newEntries = [];
      const logs = [];

      for (const client of dynamicClients) {
        if (!existingClientIds.has(client.id)) {
          const entry = {
            clientId: client.id,
            clientName: client.name || client.id,
            email: client.email || "–",
            patrimonio: client.patrimonio?.total || 0,
            feeAnual: client.feeAnual ?? 0.8,
            refMes: currentMesRef,
            vencimento: currentVenc,
            status: "Pendente",
            pagoEm: null,
            obs: "",
          };
          newEntries.push(entry);
          logs.push(`Auto-gerado: ${entry.clientName} (${currentMesRef})`);

          // Persiste no Supabase
          upsertCobranca({
            client_id: entry.clientId,
            client_name: entry.clientName,
            email: entry.email,
            patrimonio: entry.patrimonio,
            fee_anual: entry.feeAnual,
            ref_mes: entry.refMes,
            vencimento: entry.vencimento,
            status: entry.status,
          }).catch((e) =>
            logWarn("AdminCobrancasTab", e.message, { context: "auto-seed cobrança" })
          );
        }
      }

      // ── AUTOMAÇÃO 2: Auto-marcar atrasados ──
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const autoOverdue = [];

      for (const c of mapped) {
        if (c.status === "Pendente") {
          const vencDate = parseVencimento(c.vencimento);
          if (vencDate && vencDate < today) {
            c.status = "Atrasado";
            autoOverdue.push(c);
            logs.push(`Auto-atrasado: ${c.clientName} (venc. ${c.vencimento})`);
            if (c.id) {
              updateCobranca(c.id, { status: "Atrasado" }).catch((e) =>
                logWarn("AdminCobrancasTab", e.message, { context: "auto-overdue" })
              );
            }
          }
        }
      }

      // ── AUTOMAÇÃO 3: Atualiza patrimônio com snapshot mais recente ──
      for (const c of mapped) {
        const client = dynamicClients.find((dc) => dc.id === c.clientId);
        if (client && client.patrimonio?.total) {
          const newPat = client.patrimonio.total;
          if (Math.abs(newPat - c.patrimonio) > 0.01 && c.status !== "Pago") {
            logs.push(
              `Patrimônio atualizado: ${c.clientName} R$ ${c.patrimonio.toLocaleString("pt-BR")} → R$ ${newPat.toLocaleString("pt-BR")}`
            );
            c.patrimonio = newPat;
            // Update na DB também
            if (c.id) {
              updateCobranca(c.id, { patrimonio: newPat }).catch((e) =>
                logWarn("AdminCobrancasTab", e.message, { context: "auto-update-patrimonio" })
              );
            }
          }
        }
      }

      const allEntries = [...mapped, ...newEntries];
      setCobrancas(allEntries);
      if (logs.length > 0) {
        setAutoLog(logs);
        logAudit("cobrancas_auto_sync", {
          new_entries: newEntries.length,
          auto_overdue: autoOverdue.length,
          total: allEntries.length,
        }).catch(() => {});
      }

      if (setting) {
        setDadosBancarios(setting);
      } else {
        setDadosBancarios({
          favorecido: "LPA Wealth Management LTDA",
          cnpj: "–",
          banco: "–",
          agencia: "–",
          conta: "–",
          pix: "–",
        });
      }
    } catch (e) {
      logWarn("AdminCobrancasTab", e.message, { context: "load cobrancas" });
    } finally {
      setLoading(false);
    }
  }, [dynamicClients, currentMesRef, currentVenc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Sync manual — re-roda automação ──────────────────────────────────────
  const handleSync = useCallback(async () => {
    setSyncing(true);
    setAutoLog([]);
    await loadData();
    setSyncing(false);
    setToast?.("Cobranças sincronizadas com sucesso!", "success");
  }, [loadData, setToast]);

  // ── Gerar próximo mês ────────────────────────────────────────────────────
  const handleGerarProximoMes = useCallback(async () => {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const nextMesRef = mesRefLabel(nextMonth);
    const nextVenc = vencimentoStr(nextMonth);

    const existingNext = new Set(
      cobrancas.filter((c) => c.refMes === nextMesRef).map((c) => c.clientId)
    );
    const newEntries = [];

    for (const client of dynamicClients) {
      if (!existingNext.has(client.id)) {
        const entry = {
          clientId: client.id,
          clientName: client.name || client.id,
          email: client.email || "–",
          patrimonio: client.patrimonio?.total || 0,
          feeAnual: client.feeAnual ?? 0.8,
          refMes: nextMesRef,
          vencimento: nextVenc,
          status: "Pendente",
          pagoEm: null,
          obs: "",
        };
        newEntries.push(entry);
        upsertCobranca({
          client_id: entry.clientId,
          client_name: entry.clientName,
          email: entry.email,
          patrimonio: entry.patrimonio,
          fee_anual: entry.feeAnual,
          ref_mes: entry.refMes,
          vencimento: entry.vencimento,
          status: entry.status,
        }).catch((e) => logWarn("AdminCobrancasTab", e.message, { context: "gerar próximo mês" }));
      }
    }

    if (newEntries.length > 0) {
      setCobrancas((prev) => [...prev, ...newEntries]);
      setToast?.(`${newEntries.length} cobranças geradas para ${nextMesRef}`, "success");
      logAudit("cobrancas_next_month_generated", {
        month: nextMesRef,
        count: newEntries.length,
      }).catch(() => {});
    } else {
      setToast?.(`Cobranças de ${nextMesRef} já existem`, "info");
    }
  }, [cobrancas, dynamicClients, setToast]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleStatusChange = async (idx, newStatus) => {
    const c = cobrancas[idx];
    const pagoEm = newStatus === "Pago" ? new Date().toLocaleDateString("pt-BR") : null;
    setCobrancas((cb) => cb.map((x, j) => (j === idx ? { ...x, status: newStatus, pagoEm } : x)));
    if (c.id) {
      updateCobranca(c.id, { status: newStatus, pago_em: pagoEm }).catch((e) =>
        logWarn("AdminCobrancasTab", e.message, { context: "updateCobranca" })
      );
    }
  };

  const handleFieldChange = (idx, field, value) => {
    setCobrancas((cb) => cb.map((x, j) => (j === idx ? { ...x, [field]: value } : x)));
  };

  const handleAddRow = () => {
    setCobrancas((cb) => [
      ...cb,
      {
        clientId: "NEW" + Date.now(),
        clientName: "Novo Cliente",
        email: "—",
        patrimonio: 0,
        feeAnual: 1.0,
        refMes: currentMesRef,
        vencimento: currentVenc,
        status: "Pendente",
        pagoEm: null,
        obs: "",
      },
    ]);
  };

  // ── Filtro por mês ───────────────────────────────────────────────────────
  const [filterMes, setFilterMes] = useState("current");

  const mesesDisponiveis = useMemo(() => {
    const set = new Set(cobrancas.map((c) => c.refMes));
    return [...set].sort();
  }, [cobrancas]);

  const filteredCobrancas = useMemo(() => {
    if (filterMes === "all") return cobrancas;
    if (filterMes === "current") return cobrancas.filter((c) => c.refMes === currentMesRef);
    return cobrancas.filter((c) => c.refMes === filterMes);
  }, [cobrancas, filterMes, currentMesRef]);

  if (loading) {
    return <SkeletonTable rows={5} cols={8} />;
  }

  const bankData = dadosBancarios || {
    favorecido: "–",
    cnpj: "–",
    banco: "–",
    agencia: "–",
    conta: "–",
    pix: "–",
  };

  return (
    <div>
      {/* Auto-sync log */}
      {autoLog.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: "12px 16px",
            background: T.infoBg || "#DBEAFE",
            borderRadius: RADIUS.md,
            border: `1px solid ${T.info || "#2563EB"}30`,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: T.info || "#2563EB",
              marginBottom: 6,
            }}
          >
            ⚡ Automação executada ({autoLog.length} ações)
          </div>
          {autoLog.map((log, i) => (
            <div key={i} style={{ fontSize: 11, color: T.sub, lineHeight: 1.6 }}>
              {log}
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      {(() => {
        const source = filteredCobrancas;
        const calcValor = (c) =>
          c.valor > 0
            ? c.valor
            : Math.round(((c.patrimonio * (c.feeAnual / 100)) / 12) * 100) / 100;
        const total = source.reduce((s, c) => s + calcValor(c), 0);
        const recebido = source
          .filter((c) => c.status === "Pago")
          .reduce((s, c) => s + calcValor(c), 0);
        const pendente = source
          .filter((c) => c.status === "Pendente")
          .reduce((s, c) => s + calcValor(c), 0);
        const atrasado = source
          .filter((c) => c.status === "Atrasado")
          .reduce((s, c) => s + calcValor(c), 0);
        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4,1fr)",
              gap: 14,
              marginBottom: 22,
            }}
          >
            {[
              { label: "Receita Mensal Total", v: fmtR(total), icon: "💼", cor: T.text },
              { label: "Recebido", v: fmtR(recebido), icon: "✅", cor: "#16A34A" },
              { label: "Pendente", v: fmtR(pendente), icon: "⏳", cor: "#D97706" },
              { label: "Atrasado", v: fmtR(atrasado), icon: "🔴", cor: "#DC2626" },
            ].map((s) => (
              <Card key={s.label} T={T} style={{ padding: "16px 20px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: T.sub,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        margin: "0 0 4px",
                      }}
                    >
                      {s.label}
                    </p>
                    <p style={{ fontSize: 18, fontWeight: 800, color: s.cor, margin: 0 }}>{s.v}</p>
                  </div>
                  <span style={{ fontSize: 22 }}>{s.icon}</span>
                </div>
              </Card>
            ))}
          </div>
        );
      })()}

      {/* Dados bancários LPA */}
      <Card T={T} style={{ marginBottom: 18 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 14,
          }}
        >
          <STitle
            title="🏦 Dados Bancários LPA (para cobranças)"
            sub="Dados armazenados no Supabase (company_settings)"
            T={T}
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>
          {[
            ["Favorecido", bankData.favorecido],
            ["CNPJ", bankData.cnpj],
            ["Banco", bankData.banco],
            ["Ag. / Conta", `${bankData.agencia} / ${bankData.conta}`],
            ["PIX", bankData.pix],
          ].map(([k, v]) => (
            <div
              key={k}
              style={{
                background: T.bg,
                borderRadius: RADIUS.sm,
                padding: "10px 13px",
                border: `1px solid ${T.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: T.muted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  marginBottom: 3,
                }}
              >
                {k}
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, wordBreak: "break-all" }}>
                {v}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Tabela de cobranças */}
      <Card T={T}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <STitle
            title="Gestão de Cobranças – Fee Based Mensal"
            sub="Cobranças geradas automaticamente para novos clientes · Atrasados marcados por vencimento"
            T={T}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {/* Filtro por mês */}
            <select
              value={filterMes}
              onChange={(e) => setFilterMes(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: RADIUS.sm,
                border: `1px solid ${T.border}`,
                fontSize: 12,
                background: T.bg,
                color: T.text,
              }}
            >
              <option value="current">Mês Atual</option>
              <option value="all">Todos os Meses</option>
              {mesesDisponiveis.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <button
              onClick={handleSync}
              disabled={syncing}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: "none",
                cursor: syncing ? "wait" : "pointer",
                background: T.accent || "#7C3AED",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                opacity: syncing ? 0.7 : 1,
              }}
            >
              {syncing ? "⏳ Sincronizando..." : "🔄 Sincronizar"}
            </button>
            <button
              onClick={handleGerarProximoMes}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                background: T.info || "#0369A1",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              📅 Gerar Próximo Mês
            </button>
            <button
              onClick={handleAddRow}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                background: T.accent,
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              + Adicionar linha
            </button>
          </div>
        </div>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
            <thead>
              <tr>
                {[
                  "Cliente",
                  "E-mail",
                  "Ref. Mês",
                  "Patrimônio (base)",
                  "Fee a.a.",
                  "Valor Mensal",
                  "Vencimento",
                  "Status",
                  "Ações",
                ].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    style={{
                      textAlign: "left",
                      padding: "9px 12px",
                      borderBottom: `2px solid ${T.border}`,
                      color: T.sub,
                      fontSize: 10,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredCobrancas.map((c, i) => {
                const valorM =
                  c.valor > 0
                    ? c.valor
                    : Math.round(((c.patrimonio * (c.feeAnual / 100)) / 12) * 100) / 100;
                const stCor =
                  c.status === "Pago" ? "#16A34A" : c.status === "Atrasado" ? "#DC2626" : "#D97706";
                const stBg =
                  c.status === "Pago" ? "#DCFCE7" : c.status === "Atrasado" ? "#FEE2E2" : "#FEF3C7";
                // Find the index in the full cobrancas array for handlers
                const realIdx = cobrancas.indexOf(c);
                return (
                  <tr
                    key={c.clientId + c.refMes}
                    style={{ background: i % 2 === 0 ? T.row1 : T.row2 }}
                  >
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: T.text }}>
                      {c.clientName}
                    </td>
                    <td style={{ padding: "10px 12px", color: T.sub, fontSize: 12 }}>{c.email}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <Badge text={c.refMes} color={T.info || "#2563EB"} />
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <CurrencyInput
                        value={c.patrimonio}
                        onChange={(v) => handleFieldChange(realIdx, "patrimonio", v)}
                        style={{
                          width: 110,
                          padding: "4px 8px",
                          borderRadius: RADIUS.sm,
                          border: `1px solid ${T.border}`,
                          fontSize: 12,
                          background: T.bg,
                          color: T.text,
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="number"
                          value={c.feeAnual}
                          min={0}
                          max={5}
                          step={0.05}
                          onChange={(e) =>
                            handleFieldChange(realIdx, "feeAnual", Number(e.target.value))
                          }
                          style={{
                            width: 60,
                            padding: "4px 8px",
                            borderRadius: RADIUS.sm,
                            border: `1px solid ${T.border}`,
                            fontSize: 12,
                            background: T.bg,
                            color: T.text,
                            outline: "none",
                          }}
                        />
                        <span style={{ fontSize: 11, color: T.muted }}>% a.a.</span>
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: T.text }}>
                      R${" "}
                      {valorM.toLocaleString("pt-BR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td style={{ padding: "10px 12px", color: T.sub, fontSize: 12 }}>
                      {c.vencimento}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <select
                        value={c.status}
                        onChange={(e) => handleStatusChange(realIdx, e.target.value)}
                        style={{
                          padding: "4px 8px",
                          borderRadius: RADIUS.sm,
                          border: `1.5px solid ${stCor}`,
                          fontSize: 11,
                          fontWeight: 700,
                          background: stBg,
                          color: stCor,
                          outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        {["Pendente", "Pago", "Atrasado"].map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {(c.status === "Pendente" || c.status === "Atrasado") && (
                          <button
                            onClick={() => setPaymentModalCobranca(c)}
                            title="Iniciar pagamento"
                            style={{
                              padding: "5px 11px",
                              borderRadius: RADIUS.sm,
                              border: "none",
                              cursor: "pointer",
                              background: "#005EB8",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 11,
                            }}
                          >
                            💳 Cobrar
                          </button>
                        )}
                        <button
                          onClick={() => setLembreteFor(c)}
                          style={{
                            padding: "5px 11px",
                            borderRadius: RADIUS.sm,
                            border: "none",
                            cursor: "pointer",
                            background: T.accent,
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        >
                          💸 Lembrete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredCobrancas.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 24, textAlign: "center", color: T.muted }}>
                    Nenhuma cobrança para o período selecionado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Totalizador */}
        <div
          style={{
            marginTop: 14,
            padding: "10px 16px",
            borderRadius: 9,
            background: T.bg,
            border: `1px solid ${T.border}`,
            display: "flex",
            gap: 28,
            flexWrap: "wrap",
          }}
        >
          <div>
            <span style={{ fontSize: 11, color: T.muted }}>Fee médio: </span>
            <strong style={{ fontSize: 13, color: T.text }}>
              {(
                filteredCobrancas.reduce((s, c) => s + c.feeAnual, 0) /
                Math.max(filteredCobrancas.length, 1)
              ).toFixed(2)}
              % a.a.
            </strong>
          </div>
          <div>
            <span style={{ fontSize: 11, color: T.muted }}>Patrimônio total gerido: </span>
            <strong style={{ fontSize: 13, color: T.text }}>
              R${" "}
              {filteredCobrancas
                .reduce((s, c) => s + c.patrimonio, 0)
                .toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </strong>
          </div>
          <div>
            <span style={{ fontSize: 11, color: T.muted }}>Receita mensal total: </span>
            <strong style={{ fontSize: 13, color: T.accent }}>
              R${" "}
              {filteredCobrancas
                .reduce(
                  (s, c) =>
                    s +
                    (c.valor > 0
                      ? c.valor
                      : Math.round(((c.patrimonio * (c.feeAnual / 100)) / 12) * 100) / 100),
                  0
                )
                .toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </strong>
          </div>
          <div>
            <span style={{ fontSize: 11, color: T.muted }}>Receita anual projetada: </span>
            <strong style={{ fontSize: 13, color: T.text }}>
              R${" "}
              {filteredCobrancas
                .reduce(
                  (s, c) => s + (c.valor > 0 ? c.valor * 12 : c.patrimonio * (c.feeAnual / 100)),
                  0
                )
                .toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </strong>
          </div>
        </div>
      </Card>

      {/* Payment Modal */}
      {paymentModalCobranca && (
        <PaymentModal
          cobranca={paymentModalCobranca}
          onClose={() => setPaymentModalCobranca(null)}
          onPaymentSuccess={() => {
            loadData();
          }}
          setToast={setToast}
          T={T}
        />
      )}
    </div>
  );
}

AdminCobrancasTab.propTypes = {
  dynamicClients: PropTypes.array.isRequired,
  setLembreteFor: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
  T: PropTypes.object.isRequired,
};
